import { RadioButton, RadioButtonGroup } from '@carbon/react';
import { DatePicker } from 'components/date-picker';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import styles from './date-filter.module.scss';

// Import SearchParams from your existing search-atom
import { SearchParams } from 'atoms/search-atom';

// Define the date range structure
interface DateRange {
  start?: string;
  end?: string;
}

// Generic constraint for search params that must have date fields
interface DateFilterableParams extends SearchParams {
  created_at?: DateRange;
  updated_at?: DateRange;
}
// Define the SetAtom type
type SetAtom<P> = (updater: (current: P | undefined) => P | undefined) => void;

interface DateFilterProps<T extends DateFilterableParams> {
  setSearchPayload: SetAtom<T>;
}

function DateFilter<T extends DateFilterableParams>({
  setSearchPayload,
}: DateFilterProps<T>): React.ReactElement {
  const [filterOption, setFilterOption] = useState('none');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [startDateError, setStartDateError] = useState('');
  const [endDateError, setEndDateError] = useState('');
  const location = useLocation();
  const history = useHistory();

  // Initialize from URL params
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const dateType = params.get('dateFilterType') || 'none';
    setFilterOption(dateType);

    if (dateType !== 'none') {
      const newStartDate = params.get(`${dateType}_start`) || '';
      const newEndDate = params.get(`${dateType}_end`) || '';

      // Validate dates from URL
      if (newStartDate && !isValidDate(newStartDate)) {
        setStartDateError('Invalid date format');
      } else {
        setStartDate(newStartDate);
        setStartDateError('');
      }

      if (newEndDate && !isValidDate(newEndDate)) {
        setEndDateError('Invalid date format');
      } else {
        setEndDate(newEndDate);
        setEndDateError('');
      }

      // Check date range
      if (
        newStartDate &&
        newEndDate &&
        isValidDate(newStartDate) &&
        isValidDate(newEndDate)
      ) {
        validateDateRange(newStartDate, newEndDate);
      }
    }
  }, [location.search]);

  // This effect runs when user makes changes to dates or filter option
  useEffect(() => {
    // Only update if there are no errors
    if (!startDateError && !endDateError) {
      // Only make API call when both dates are selected and filter is not 'none'
      if (filterOption !== 'none' && startDate && endDate) {
        updateSearch();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterOption, startDate, endDate, startDateError, endDateError]);

  // Validate if a string is a valid date in YYYY-MM-DD format
  const isValidDate = (dateStr: string): boolean => {
    // First check if the string matches the expected format
    if (!dateStr || !dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return false;
    }

    // Create a valid Moment object with strict parsing
    const momentDate = moment(dateStr, 'YYYY-MM-DD', true);

    // Comprehensive validation checks:
    // 1. Moment must consider it valid
    // 2. Year must be reasonable (between 1 and 9999)
    // 3. Month and day must be valid for that year
    return (
      // Ensures the date actually exists
      momentDate.isValid() &&
      momentDate.year() >= 1 &&
      momentDate.year() <= 9999 &&
      momentDate.format('YYYY-MM-DD') === dateStr
    );
  };

  // Validate that end date is not before start date
  const validateDateRange = (start: string, end: string): boolean => {
    if (!start || !end) {
      return true;
    }

    const startMoment = moment(start, 'YYYY-MM-DD', true);
    const endMoment = moment(end, 'YYYY-MM-DD', true);

    if (!startMoment.isValid() || !endMoment.isValid()) {
      return false;
    }

    if (endMoment.isBefore(startMoment)) {
      setEndDateError('End date cannot be before start date');
      return false;
    } else {
      setEndDateError('');
      return true;
    }
  };

  // Function to update search parameters
  const updateSearch = () => {
    // Only proceed if both dates are selected and valid
    if (
      !(startDate && endDate) ||
      !validateDateRange(startDate, endDate) ||
      startDateError ||
      endDateError
    ) {
      return;
    }

    const params = new URLSearchParams(location.search);

    // Clear existing date filters
    params.delete('dateFilterType');
    params.delete('created_start');
    params.delete('created_end');
    params.delete('updated_start');
    params.delete('updated_end');

    // Set new date filters if applicable
    if (filterOption !== 'none') {
      params.set('dateFilterType', filterOption);

      if (startDate) {
        params.set(`${filterOption}_start`, startDate);
      }

      if (endDate) {
        params.set(`${filterOption}_end`, endDate);
      }
    }

    params.set('page', '1');
    history.replace({ search: params.toString() });

    // Update search payload using the functional update pattern
    setSearchPayload((current) => {
      if (!current) return undefined;

      const updated = { ...current, page: 1 } as T;

      // Reset date filters
      updated.created_at = undefined;
      updated.updated_at = undefined;

      // Set appropriate date filter with current user timestamp
      if (filterOption === 'created' && startDate && endDate) {
        updated.created_at = {
          start: addTimestampToDate(startDate),
          end: addTimestampToDate(endDate),
        };
      } else if (filterOption === 'updated' && startDate && endDate) {
        updated.updated_at = {
          start: addTimestampToDate(startDate),
          end: addTimestampToDate(endDate),
        };
      }
      return updated;
    });
  };

  // Helper function to add current timestamp to date string using Moment
  const addTimestampToDate = (dateStr: string): string => {
    if (!dateStr || !moment(dateStr, 'YYYY-MM-DD', true).isValid()) {
      return moment.utc().toISOString();
    }

    // Create a date in UTC to avoid timezone issues
    const dateMoment = moment.utc(dateStr, 'YYYY-MM-DD');

    // Get current time in UTC
    const now = moment.utc();

    // Set the time portion from the current UTC time
    dateMoment.set({
      hour: now.utc().hour(),
      minute: now.utc().minute(),
      second: now.utc().second(),
      millisecond: now.utc().millisecond(),
    });

    return dateMoment.toISOString();
  };

  // Common date handling function
  const handleDateChange = (
    date: Date[],
    setDate: (date: string) => void,
    setError: (error: string) => void,
    oppositeRangeDate?: string,
    isStartDate = false
  ) => {
    setError('');

    if (!date[0]) {
      setDate('');
      return;
    }

    // Use moment to format the date in UTC
    const formattedDate = moment.utc(date[0]).format('YYYY-MM-DD');

    if (!isValidDate(formattedDate)) {
      setError('Invalid date');
      return;
    }

    setDate(formattedDate);

    // Validate the date range if we have both dates
    if (oppositeRangeDate) {
      validateDateRange(
        isStartDate ? formattedDate : oppositeRangeDate,
        isStartDate ? oppositeRangeDate : formattedDate
      );
    }
  };

  // Handle start date change
  const handleStartDateChange = (date: Date[]) => {
    handleDateChange(date, setStartDate, setStartDateError, endDate, true);
  };

  // Handle end date change
  const handleEndDateChange = (date: Date[]) => {
    handleDateChange(date, setEndDate, setEndDateError, startDate, false);
  };

  // Handle filter option change
  const handleFilterChange = (value: string | number | undefined) => {
    const filterValue = value as string;
    setStartDateError('');
    setEndDateError('');

    setFilterOption(filterValue);
    // Get current URL parameters
    const params = new URLSearchParams(location.search);

    // If switching to 'none', clear the dates and URL parameters
    if (filterValue === 'none') {
      setStartDate('');
      setEndDate('');

      // Clear date filter params from URL
      params.delete('dateFilterType');
      params.delete('created_start');
      params.delete('created_end');
      params.delete('updated_start');
      params.delete('updated_end');

      setSearchPayload((current) => {
        if (!current) return undefined;

        const updated = { ...current } as T;
        updated.created_at = undefined;
        updated.updated_at = undefined;
        return updated;
      });

      // Update URL immediately
      history.replace({ search: params.toString() });
    } else {
      history.replace({ search: params.toString() });
    }
  };

  return (
    <div className={styles.dateFilterContainer}>
      <h1 className={styles.heading}>Date Filter</h1>
      <div className={styles.contentWrapper}>
        <div className={styles.datePickersSection}>
          <div className={styles.datePickerWrapper}>
            <DatePicker
              data-testid="start-date"
              id="start-date"
              labelText=""
              size="md"
              value={startDate}
              onChange={handleStartDateChange}
              disabled={filterOption === 'none'}
            />
            {startDateError && (
              <div className={styles.errorText}>{startDateError}</div>
            )}
          </div>

          <div className={styles.datePickerWrapper}>
            <DatePicker
              data-testid="end-date"
              id="end-date"
              labelText=""
              size="md"
              value={endDate}
              onChange={handleEndDateChange}
              disabled={filterOption === 'none'}
            />
            {endDateError && (
              <div className={styles.errorText}>{endDateError}</div>
            )}
          </div>
        </div>

        <RadioButtonGroup
          name="date-filter-options"
          orientation="vertical"
          valueSelected={filterOption}
          onChange={handleFilterChange}
          className={styles.radioGroup}
        >
          <RadioButton id="none-option" labelText="None" value="none" />
          <RadioButton
            id="created-option"
            labelText="Created Date"
            value="created"
          />
          <RadioButton
            id="updated-option"
            labelText="Updated Date"
            value="updated"
          />
        </RadioButtonGroup>
      </div>
    </div>
  );
}

export default DateFilter;
